// The site camera's phone (Canvas stage 11, S1; context/22-canvas-site-camera.md). Someone at the property films with
// their phone's own browser: the back camera and their voice go out, and nothing else — the pass carries no data grant,
// so this page could not draw, point or steer the tour even if it tried. The sales manager decides when the picture is
// on the stage. While it is, his marks and pointer on it (and anyone's with a pen, and the customer's pointer) are drawn
// over the camera here too, in the frame's own units, so the person filming can see where to walk.
import { createLocalTracks, RoomEvent, Track, VideoPresets } from "livekit-client";
import { firstName, h, toast } from "./dom.js";
import { icon } from "./icons.js";
import { formatWhen } from "./format.js";
import { meetingWords } from "./host-words.js";
import { fitPlans, paintPointer } from "./canvas/render.js";
import { TOPIC, decode, roleOf } from "./canvas/sync.js";
import { InkBook, LIMITS, cleanStroke, colourOf, mayDraw, paintInk, paintPeers, peerFrom, readCapped, allowedDeletes } from "./canvas/ink.js";

const PAGE = "share"; // the glass: the one page this screen draws

// The join page: whose meeting, a name, the back camera already showing, and one button.
export function siteLobby(root, info) {
  return new Promise((resolve) => {
    const { meeting, you } = info;
    const host = firstName(meeting.hostName) || meetingWords(meeting).hostFallback;
    const state = { micOn: true, audioTrack: null, videoTrack: null };
    const video = h("video", { autoplay: true, playsinline: true, muted: true });
    const preview = h("div", { class: "st-preview" }, video);
    const micBtn = h("button", { class: "round", type: "button", onclick: () => setMic(!state.micOn) });
    const nameField = you.needsName ? h("input", { class: "cl-name", maxlength: "60", autocomplete: "name", placeholder: "Your name", "aria-label": "Your name" }) : null;
    if (nameField && you.name) nameField.value = you.name; // back from Rejoin: the name given the first time
    const errorBox = h("p", { class: "notice", role: "alert", hidden: true });
    const goBtn = h("button", { class: "btn", type: "button", text: "Checking your camera…", disabled: true, onclick: () => go() });
    root.replaceChildren(h("div", { class: "page st-lobby" },
      h("header", { class: "page-head" }, h("span", { class: "brand canvas-brand" }, h("b", { text: "Walkin" }), h("span", { class: "by", text: " by Clikin.ai" }))),
      h("main", { class: "page-main" },
        h("p", { class: "eyebrow", text: ["Site camera", meeting.project].filter(Boolean).join(" · ") }),
        h("h1", { class: "statement", text: `For ${host}'s Walkin meeting` }),
        h("p", { class: "lede", text: `${host} will show what your camera sees to the customer, and can point at it. Hold the phone steady and walk slowly. Your voice is heard too.` }),
        preview, h("div", { class: "toggles" }, micBtn),
        // The same words the customer reads, because this phone's microphone is recorded like anyone else's (ms-record).
        // Ravi, 26 Sep: no separate transcription notice — his recording sentence covers it once recording is
        // on (D145, audio only), and he is keeping recording. Until then live assist transcribes and
        // nothing here says so; that window is his call, made knowingly.
        meeting.recorded ? h("p", { class: "notice", text: "We record this meeting to improve customer experience and serve you better." }) : null,
        nameField, errorBox, goBtn,
        h("p", { class: "fine", text: `${formatWhen(meeting.startsAt)} · No app needed. Nothing is sent until you press Start.` }))));
    document.title = "Site camera · Walkin";
    paint();
    start();

    async function start() {
      const w = info.window?.state || "open";
      if (w !== "open") {
        goBtn.textContent = { early: "Not open yet", over: "This meeting is over", ended: "This meeting has ended", cancelled: "This meeting was cancelled" }[w] || "Not open";
        showError(w === "early" ? `The meeting opens at ${formatWhen(info.window.opensAt)}. This same link will work then.` : `Ask ${host} for a new link if the meeting is still on.`);
        return;
      }
      try {
        const tracks = await createLocalTracks({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
          video: { resolution: VideoPresets.h720.resolution, facingMode: "environment" },
        });
        for (const t of tracks) {
          if (t.kind === "audio") state.audioTrack = t;
          if (t.kind === "video") state.videoTrack = t;
        }
      } catch (err) {
        showError(err && err.name === "NotAllowedError"
          ? "Your browser is blocking the camera. Allow it for this site (the icon beside the address), then open the link again."
          : "We could not reach your camera. Close other apps using it and open the link again.");
        return;
      }
      if (!state.videoTrack) { showError("We could not reach your camera. Open the link again."); return; }
      state.videoTrack.attach(video);
      goBtn.disabled = false;
      goBtn.textContent = "Start the camera";
      if (info.notice) showError(info.notice);
      paint();
    }
    async function setMic(on) {
      state.micOn = on && Boolean(state.audioTrack);
      if (state.audioTrack) on ? await state.audioTrack.unmute() : await state.audioTrack.mute();
      paint();
    }
    function paint() {
      micBtn.innerHTML = icon(state.micOn ? "mic" : "micOff");
      micBtn.classList.toggle("is-off", !state.micOn);
      micBtn.setAttribute("aria-label", state.micOn ? "Turn microphone off" : "Turn microphone on");
    }
    function showError(text) { errorBox.textContent = text; errorBox.hidden = !text; }
    function go() {
      const name = you.needsName ? String(nameField.value || "").trim() : you.name;
      if (!name) { showError("Please give your name first, so the others know who is filming."); nameField.focus(); return; }
      showError("");
      goBtn.disabled = true;
      goBtn.textContent = "Starting…";
      state.videoTrack.detach(video);
      // camOn: the camera is the point of this page. listenOnly: nobody's video comes down to a phone on 4G at the site.
      resolve({ name, micOn: state.micOn, camOn: true, audioTrack: state.audioTrack, videoTrack: state.videoTrack, listenOnly: true });
    }
  });
}

// In the meeting: the camera, full screen at its own shape, with the glass's marks and pointers over it.
export function runSite(root, { info, session, joined }) {
  const { room } = session;
  const hostFirst = firstName(info.meeting.hostName) || meetingWords(info.meeting).hostFallback;
  const me = () => room.localParticipant.identity;
  const st = { cam: null, facing: "environment", ptr: null }; // cam: the site camera on his stage, from his state
  const book = new InkBook();
  const strokes = new Map(); // strokes in progress, by id
  const peers = new Map();   // other people's pointers, by identity
  let holders = new Set();

  const video = h("video", { class: "share-src", autoplay: true, playsinline: true, muted: true });
  const fit = h("div", { class: "cv-fit", "data-w": "720", "data-h": "1280" }, video);
  fit.insertAdjacentHTML("beforeend", '<svg class="ink" viewBox="0 0 720 1280" preserveAspectRatio="none" aria-hidden="true"></svg><div class="ink-lbls"></div><div class="ptr" hidden><span></span></div>');
  fit.querySelector(".ptr span").textContent = hostFirst;
  const stage = h("div", { class: "cv share st-stage" }, h("div", { class: "cv-center" }, fit));
  const status = h("div", { class: "st-top", role: "status" });
  const micBtn = h("button", { class: "round", type: "button", onclick: () => toggleMic() });
  const flipBtn = h("button", { class: "round", type: "button", "aria-label": "Switch between the back and front camera", onclick: () => flip() });
  flipBtn.innerHTML = icon("flip");
  const leaveBtn = h("button", { class: "round st-leave", type: "button", "aria-label": "Stop the camera and leave", onclick: () => session.leave() });
  leaveBtn.innerHTML = icon("leave");
  const bar = h("div", { class: "st-bar" }, micBtn, flipBtn, leaveBtn);
  root.replaceChildren(h("div", { class: "st" }, stage, status, bar));
  document.body.classList.add("ms-dark");
  document.title = "Site camera · Walkin";
  joined.videoTrack?.attach(video);

  const sized = () => {
    if (!video.videoWidth || !video.videoHeight) return;
    fit.dataset.w = video.videoWidth;
    fit.dataset.h = video.videoHeight;
    fit.querySelector(".ink").setAttribute("viewBox", `0 0 ${video.videoWidth} ${video.videoHeight}`);
    fitPlans(stage);
    paintGlass();
  };
  video.addEventListener("loadedmetadata", sized);
  video.addEventListener("resize", sized);
  const onResize = () => { fitPlans(stage); paintGlass(); };
  window.addEventListener("resize", onResize);

  const shown = () => st.cam === me();
  const customer = () => [...room.remoteParticipants.values()].find((p) => roleOf(p) === "customer") || null;
  const hostHere = () => [...room.remoteParticipants.values()].some((p) => roleOf(p) === "host");
  function paintStatus() {
    const who = firstName(customer()?.name) || "the customer";
    status.classList.toggle("on", shown());
    status.replaceChildren(h("i"), h("b", { text: !hostHere() ? `Waiting for ${hostFirst}` : shown() ? `Showing to ${who}` : `${hostFirst} can see your camera` }),
      h("small", { text: shown() ? "Walk slowly · the dots and marks show where they are looking" : `${info.meeting.project || "Walkin"} · site camera` }));
    const on = room.localParticipant.isMicrophoneEnabled;
    micBtn.innerHTML = icon(on ? "mic" : "micOff");
    micBtn.classList.toggle("is-off", !on);
    micBtn.setAttribute("aria-label", on ? "Mute" : "Unmute");
  }
  // The glass is drawn only while this camera is on the stage: his marks at other times are on another page.
  function paintGlass() {
    const on = shown();
    paintInk(stage, on ? book.get(PAGE) : [], on ? [...strokes.values()] : []);
    paintPointer(stage, on ? st.ptr : null);
    paintPeers(stage, on ? [...peers.values()].map((p) => ({ ...p, colour: colourOf(p.id) })) : []);
  }

  async function toggleMic() {
    await room.localParticipant.setMicrophoneEnabled(!room.localParticipant.isMicrophoneEnabled);
    paintStatus();
  }
  async function flip() {
    const pub = room.localParticipant.getTrackPublication(Track.Source.Camera);
    const track = pub?.track;
    if (!track) return;
    const next = st.facing === "environment" ? "user" : "environment";
    try { await track.restartTrack({ facingMode: next, resolution: VideoPresets.h720.resolution }); st.facing = next; }
    catch {
      // restartTrack lets go of the old camera before asking for the new one: put the one it had back (audit S1, Low).
      try { await track.restartTrack({ facingMode: st.facing, resolution: VideoPresets.h720.resolution }); } catch { /* reported below */ }
      toast("This phone has no other camera.");
    }
  }

  // Everyone's voice, nobody's picture: the phone is on mobile data at the site.
  const wantAudio = (pub) => { if (pub.kind === Track.Kind.Audio && !pub.isSubscribed) pub.setSubscribed(true); };
  for (const p of room.remoteParticipants.values()) for (const pub of p.trackPublications.values()) wantAudio(pub);

  function onData(payload, participant, _kind, topic) {
    if (!participant) return;
    const body = decode(payload);
    const isHost = roleOf(participant) === "host";
    if (topic === TOPIC.STATE && isHost) {
      const was = st.cam;
      st.cam = body && typeof body.cam === "string" ? body.cam : null;
      // A change of stage starts clean: a pointer or a stroke from the page before is not on this picture.
      if (was !== st.cam) { strokes.clear(); st.ptr = null; peers.clear(); }
      paintStatus();
      paintGlass();
    } else if (topic === TOPIC.PEN && isHost) {
      holders = new Set((Array.isArray(body?.holders) ? body.holders : []).filter((i) => typeof i === "string").slice(0, 12));
    } else if (topic === TOPIC.PTR && isHost) {
      st.ptr = body && Number.isFinite(body.x) && Number.isFinite(body.y) ? { x: Math.max(0, Math.min(100, body.x)), y: Math.max(0, Math.min(100, body.y)) } : null;
      paintGlass();
    } else if (topic === TOPIC.PEER_PTR && !isHost) {
      const p = peerFrom(body, participant, firstName(participant.name) || "Guest");
      if (p && p.page === PAGE && Number.isFinite(p.x)) peers.set(p.id, p);
      else peers.delete(participant.identity);
      paintGlass();
    } else if (topic === TOPIC.INK_LIVE && mayDraw(participant, holders)) {
      if (!body || body.page !== PAGE || typeof body.id !== "string") return;
      const prev = strokes.get(body.id);
      if (prev && prev.by !== participant.identity) return;
      if (body.cancel) { strokes.delete(body.id); paintGlass(); return; }
      if (!prev && strokes.size >= 24 && !isHost) return;
      const pts = body.tool === "arrow" ? body.pts : [...(prev ? prev.pts : []), ...(Array.isArray(body.pts) ? body.pts : [])];
      const s = cleanStroke({ id: body.id, tool: body.tool, pts: Array.isArray(pts) ? pts.slice(0, LIMITS.points) : [], by: participant.identity });
      if (!s || s.tool === "label" || (!isHost && !["pen", "hl"].includes(s.tool))) return;
      strokes.set(body.id, { ...s, fading: Boolean(body.end) });
      if (body.end) setTimeout(() => { if (strokes.get(body.id)?.fading) { strokes.delete(body.id); paintGlass(); } }, 1700);
      paintGlass();
    } else if (topic === TOPIC.INK_OP && mayDraw(participant, holders)) {
      if (!body || body.page !== PAGE) return;
      if (body.op === "del" && Array.isArray(body.ids)) book.remove(PAGE, allowedDeletes(book.get(PAGE), body.ids, participant.identity, isHost));
      if (body.op === "clear" && isHost) { book.clear(PAGE); strokes.clear(); }
      paintGlass();
    }
  }
  // Finished marks on the glass, as a text stream: one stroke from anyone allowed, the whole glass from the host.
  room.registerTextStreamHandler(TOPIC.INK, async (reader, from) => {
    const who = { identity: from?.identity };
    if (!mayDraw(who, holders)) return;
    const isHost = roleOf(who) === "host";
    if (!isHost && (reader.info?.size || 0) > LIMITS.stream) return;
    let text;
    try { text = isHost ? await reader.readAll() : await readCapped(reader, LIMITS.stream); } catch { return; }
    let body;
    try { body = JSON.parse(text); } catch { return; }
    if (!body || body.page !== PAGE) return; // books and other pages are the tour's, not this screen's
    if (body.stroke && typeof body.stroke === "object") {
      book.add(PAGE, { ...body.stroke, by: from.identity });
      if (strokes.get(body.stroke.id)?.by === from.identity) strokes.delete(body.stroke.id);
    }
    if (Array.isArray(body.strokes) && isHost) { book.clear(PAGE); for (const s of body.strokes.slice(0, LIMITS.strokes)) book.add(PAGE, s); }
    paintGlass();
  });

  const relayout = () => paintStatus();
  room
    .on(RoomEvent.DataReceived, onData)
    .on(RoomEvent.TrackPublished, wantAudio)
    .on(RoomEvent.ParticipantConnected, relayout)
    .on(RoomEvent.ParticipantDisconnected, (p) => {
      peers.delete(p.identity);
      for (const [id, s] of strokes) if (s.by === p.identity) strokes.delete(id);
      // He left: nothing of his stays on the glass, and nobody's pen outlives him.
      if (roleOf(p) === "host") { st.cam = null; st.ptr = null; holders = new Set(); }
      paintStatus();
      paintGlass();
    })
    .on(RoomEvent.LocalTrackPublished, relayout)
    .on(RoomEvent.TrackMuted, relayout)
    .on(RoomEvent.TrackUnmuted, relayout);
  paintStatus();
  fitPlans(stage);

  session.ended.then(() => {
    room.unregisterTextStreamHandler(TOPIC.INK);
    window.removeEventListener("resize", onResize);
    document.body.classList.remove("ms-dark");
  });
}
